import React, { useContext, useEffect, useRef } from 'react';
import {
  CategoryLabel,
  HeaderStyledSubNav,
  HeaderStyledSubNavButton,
  HeaderStyledSubNavList,
  HeaderStyledSubNavListContainer,
  HeaderStyledSubNavTeaserWrapper,
  NamedCategoryList,
  NamedStyledUnorderedList,
} from './AudiHeaderSubNavStyles';
import AudiHeaderSubNavListItem from './AudiHeaderSubNavListItem';
import type { AudiHeaderSubNavProps } from '../../interfaces/header-components.interfaces';
import { scriptRegex } from '../../utils/string-utils';
import { getKeyboardFocusableElements, keyTypes, navigate, trapFocus } from '../../utils/a11y';
import useKeyboard from '../../hooks/use-keyboars-events';
import { SUB_NAVIGATION_CLASSNAME } from '../../utils/globals';
import type { Link } from '../../interfaces/header-response.interfaces';
import { SYS_SIZE_FIXED_400 } from '@oneaudi/unified-web-common';
import { getCategoryDetails } from '../../utils/get-category-details';
import { ServiceContext } from '../../context/ServiceContext';

const AudiHeaderSubNav: React.FC<AudiHeaderSubNavProps> = ({
  mainCategoryPosition,
  mainCategoryLabel,
  closeSubNavigation,
  onItemClick,
  headerNavigationItemsAmount,
  isSubNavOpen,
  subNavItem,
  useFootnoteReferenceServiceTextParserHook,
  useOneLayer,
}) => {
  const refContainer = useRef<HTMLDivElement>(null);
  const { headerStateService } = useContext(ServiceContext);

  const dangerousHTML = useFootnoteReferenceServiceTextParserHook(
    subNavItem?.Link?.Text?.replace(scriptRegex, ' illegal script '),
  );

  const { isBasicCategory, blocksToRender, validBlocks } = getCategoryDetails(subNavItem);

  const [focusableElements, setFocusableElements] = React.useState<HTMLElement[]>([]);
  let isSmallView = true;
  headerStateService.registerCallback((headerStateRemote) => {
    isSmallView = headerStateRemote.isMobileView;
  });

  const renderSubNavListItem = (link: Link, i: number) => {
    {
      const key = `${link?.Text}${link?.Url}`;
      return (
        <AudiHeaderSubNavListItem
          index={i}
          isSubNavOpen={isSubNavOpen}
          key={key}
          listItem={link}
          onItemClick={onItemClick}
          useFootnoteReferenceServiceTextParserHook={useFootnoteReferenceServiceTextParserHook}
          useOneLayer={useOneLayer}
        />
      );
    }
  };

  useEffect(() => {
    if (isSubNavOpen) {
      const elem = document.getElementsByClassName('submenu-open')[0] as HTMLDivElement;
      const focusedElements = getKeyboardFocusableElements(elem);
      setFocusableElements(focusedElements);
      // set initial focus on first element
      requestAnimationFrame(() => {
        focusedElements[0]?.focus();
      });
    }
  }, [isSubNavOpen]);

  useKeyboard(
    'keydown',
    (event) => {
      if (isSubNavOpen) {
        if (refContainer?.current && focusableElements.length > 0) {
          trapFocus(focusableElements, event);
          if (event.key === keyTypes.ArrowLeft || event.key === keyTypes.ArrowRight) {
            navigate(
              refContainer.current,
              event.key,
              keyTypes.ArrowRight,
              keyTypes.ArrowLeft,
              `.${SUB_NAVIGATION_CLASSNAME}`,
            );
          }
        }
        if (event.key === keyTypes.ArrowUp) {
          closeSubNavigation(event as unknown as React.MouseEvent<HTMLButtonElement>);
        }
      }
    },
    refContainer,
  );

  return (
    <HeaderStyledSubNav
      headerNavigationItemsAmount={headerNavigationItemsAmount}
      isNavigationOverflow={isSmallView}
      isSubNavOpen={isSubNavOpen}
      role="tabpanel"
      className={isSubNavOpen ? 'submenu-open' : ''}
    >
      <div>
        <HeaderStyledSubNavButton
          headerNavigationItemsAmount={headerNavigationItemsAmount}
          isNavigationOverflow={isSmallView}
          onClick={closeSubNavigation}
        >
          <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 16.9L15.5 11.5 10 6.1" fill="none" stroke="currentColor" />
          </svg>
          <span
            dangerouslySetInnerHTML={{
              __html: dangerousHTML,
            }}
          />
        </HeaderStyledSubNavButton>
        <HeaderStyledSubNavTeaserWrapper
          headerNavigationItemsAmount={headerNavigationItemsAmount}
          isNavigationOverflow={isSmallView}
          ref={refContainer}
        >
          <HeaderStyledSubNavListContainer
            categories={(!isBasicCategory && blocksToRender?.length) || 1}
            aria-label={subNavItem.Link?.Text}
            headerNavigationItemsAmount={headerNavigationItemsAmount}
            isNavigationOverflow={isSmallView}
          >
            <HeaderStyledSubNavList
              headerNavigationItemsAmount={headerNavigationItemsAmount}
              categories={(!isBasicCategory && blocksToRender?.length) || 1}
              isBasicCategory={isBasicCategory}
              isNavigationOverflow={isSmallView}
            >
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              {isBasicCategory && subNavItem?.SubNavigation?.map(renderSubNavListItem)}
              {!isBasicCategory &&
                validBlocks.map((block, i) => {
                  return (
                    <NamedCategoryList className="categoryLists" key={i}>
                      <NamedStyledUnorderedList key={i}>
                        <li style={{ listStyle: 'none' }}>
                          <CategoryLabel
                            variant={{ order: process.env.IS_LEGACY ? '2' : '3', style: 'normal' }}
                            margin={[0, 0, SYS_SIZE_FIXED_400, 0]}
                          >
                            {block.Label}
                          </CategoryLabel>
                        </li>
                        {block.SubNavigation.map(renderSubNavListItem)}
                      </NamedStyledUnorderedList>
                    </NamedCategoryList>
                  );
                })}
            </HeaderStyledSubNavList>
          </HeaderStyledSubNavListContainer>
        </HeaderStyledSubNavTeaserWrapper>
      </div>
    </HeaderStyledSubNav>
  );
};
export default AudiHeaderSubNav;
